import { useCallback, useMemo, useState } from 'react'

import {
  FormFields,
  mapApiResponseToFieldValidationErrors,
  useForm,
  ValidationError
} from '@sequencehq/utils'
import {
  isEmail,
  required,
  notInListCaseInsensitive
} from '@sequencehq/utils/validation'

import { CustomerContact } from 'CustomerContacts/types'
import {
  isSequenceApiError,
  isSequenceApiErrorResponse,
  useQuery
} from '@sequencehq/api'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'

export type CustomerContactFields = {
  name: Required<CustomerContact['name']>
  email: CustomerContact['email']
  purposes: CustomerContact['purposes']
  notificationSubscriptions: CustomerContact['notificationSubscriptions']
}

export type CustomerContactFieldsConfig = {
  name: FormFields<CustomerContactFields>['name']
  email: FormFields<CustomerContactFields>['email']
  purposes: FormFields<CustomerContactFields>['purposes']
  notificationSubscriptions: FormFields<CustomerContactFields>['notificationSubscriptions']
}

const FORM_FIELD_KEYS = ['name', 'email', 'notificationSubscriptions']

export type ContactBody = Pick<
  CustomerContact,
  'name' | 'email' | 'purposes' | 'notificationSubscriptions'
>

type UseContactModalFormProps = {
  contactId: string
  customerId: string
  fieldsValues: CustomerContactFields
  updateFields: (newFields: CustomerContactFields) => void
  createContact: (body: ContactBody) => Promise<void>
  updateContact: ({
    contactId,
    body
  }: {
    contactId: string
    body: ContactBody
  }) => Promise<void>
}

type UseContactModalForm = (props: UseContactModalFormProps) => {
  fieldsConfig: CustomerContactFieldsConfig
  formErrors: { message: string }[]
  buttonSubmit: {
    onSubmit: (callback?: () => void) => Promise<boolean>
    disabled: boolean
    isValid?: boolean
  }
  isUpdating: boolean
  resetForm: VoidFunction
}

export const useContactModalForm: UseContactModalForm = ({
  contactId,
  customerId,
  fieldsValues,
  updateFields,
  createContact,
  updateContact
}) => {
  const contactsQuery = useQuery(dashboardv20240509Client.getCustomerContacts, {
    customerId
  })

  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [isDirty, setIsDirty] = useState(false)
  const [isValid, setIsValid] = useState(true)
  const [apiErrors, setApiErrors] = useState<Record<
    string,
    ValidationError[]
  > | null>(null)

  const initialValue = useMemo((): CustomerContactFields => {
    if (contactId === 'new') {
      return fieldsValues
    }

    const contact = contactsQuery.data?.items?.find(c => c.id === contactId)

    if (!contact) {
      return fieldsValues
    }

    return {
      name: contact.name || '',
      email: contact.email,
      purposes: contact.purposes,
      notificationSubscriptions: contact.notificationSubscriptions
    }
  }, [contactId, contactsQuery.data?.items, fieldsValues])

  const { fields, queries } = useForm({
    value: initialValue,
    showValidationErrors: !isValid,
    apiErrors,
    fieldConfiguration: [
      {
        property: 'name',
        validation: [required]
      },
      {
        property: 'email',
        validation: [
          required,
          isEmail,
          notInListCaseInsensitive(
            (contactsQuery.data?.items ?? [])
              .filter(contact => contact.id !== contactId)
              .map(contact => contact.email)
          )
        ]
      },
      {
        property: 'purposes'
      },
      {
        property: 'notificationSubscriptions'
      }
    ],
    onChange: newData => {
      updateFields(newData)
      setIsDirty(true)
    }
  })

  const handleSubmit = useCallback(
    async (onSubmit?: () => void) => {
      if (!queries.isValid) {
        setIsValid(false)
        return false
      }

      setIsUpdating(true)
      setApiErrors(null)

      try {
        if (contactId === 'new') {
          await createContact({
            name: fieldsValues.name || '',
            email: fieldsValues.email,
            purposes: fieldsValues.purposes,
            notificationSubscriptions: fieldsValues.notificationSubscriptions
          })
        } else {
          await updateContact({
            contactId,
            body: {
              name: fieldsValues.name || '',
              email: fieldsValues.email,
              purposes: fieldsValues.purposes,
              notificationSubscriptions: fieldsValues.notificationSubscriptions
            }
          })
        }
      } catch (e) {
        if (isSequenceApiError(e) && isSequenceApiErrorResponse(e.response)) {
          setApiErrors(await mapApiResponseToFieldValidationErrors(e.response))
        }

        setIsValid(false)
        setIsUpdating(false)
        return false
      }

      setIsUpdating(false)
      setIsDirty(false)
      setIsValid(true)

      onSubmit?.()
      return true
    },
    [
      contactId,
      createContact,
      fieldsValues.email,
      fieldsValues.name,
      fieldsValues.notificationSubscriptions,
      fieldsValues.purposes,
      updateContact,
      queries.isValid
    ]
  )

  const resetForm = () => {
    setIsDirty(false)
    setIsValid(true)
    setApiErrors(null)
  }

  const fieldsConfig = fields

  const formErrors = useMemo(
    () =>
      Object.entries(apiErrors ?? {})
        .filter(([key]) => !FORM_FIELD_KEYS.includes(key))
        .flatMap(([, errors]) => errors),
    [apiErrors]
  )

  return {
    fieldsConfig,
    formErrors,
    buttonSubmit: {
      onSubmit: handleSubmit,
      disabled: isUpdating || !isDirty,
      isValid: queries.isValid
    },
    isUpdating,
    resetForm
  }
}
